import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';

import { RootState } from '../..';
import { getOnboardingUser, updateOnboardingUser } from './api';
import { setOnboardingUserIdToLocalStorage } from '../../../helper';

export const ONBOARDING_USER_SUBJECT_EXPERIENCE_LEVELS = {
  STARTING: 'starting',
  KNOW_BIT: 'know-bit',
  OKAY: 'ok',
  CONFIDENT: 'confident',
};

export const LEARNING_STYLE_OPTIONS = {
  WITH_DIAGRAMS_AND_VISUALS: 'with-diagrams-and-visuals',
  STEP_BY_STEP_EXPLANATION: 'step-by-step-explanation',
  WITH_PRACTICE_QUESTIONS_AND_QUIZZES: 'with-practice-questions-and-quizzes',
  NOT_SURE: 'not-sure',
};

export const ONBOARDING_USER_TESTS_EXAMS_COMING_UP_OPTIONS = {
  NOT_RIGHT_NOW: 'not-right-now',
  IN_ONE_TO_TWO_MONTHS: 'in-one-to-two-months',
  IN_THREE_TO_FOUR_MONTHS: 'in-three-to-four-months',
  IN_FIVE_PLUS_MONTHS: 'in-five-plus-months',
};

export const ONBOARDING_USER_LAST_EXAMS_STATUS_OPTIONS = {
  DID_REALLY_WELL: 'did-really-well',
  IT_WAS_OKAY: 'it-was-okay',
  FOUND_IT_DIFFICULT: 'found-it-difficult',
  HAVE_NOT_HAD_ONE_YET: 'have-not-had-one-yet',
};

export const ONBOARDING_USER_UPCOMING_EXAMS_OPTIONS = {
  MAY_JUN_2026: 'may-jun-2026',
  OCT_NOV_2026: 'oct-nov-2026',
  MAY_JUN_2027: 'may-jun-2027',
  OCT_NOV_2027: 'oct-nov-2027',
};

export interface IOnboardingUser {
  _id?: string;
  curriculum?: string;
  subject?: string;
  grade?: string;
  subjectExperienceLevel?: string;
  learningStyle?: string;
  upcomingExamStatus?: string;
}

interface IOnboardingState {
  status: 'idle' | 'loading' | 'rejected';
  onboardingUserData?: IOnboardingUser;
}

const initialState: IOnboardingState = {
  status: 'idle',
  onboardingUserData: undefined,
};

export const OnboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    resetOnboardingUserData: (state) => {
      state.onboardingUserData = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOnboardingUser.fulfilled, (state, action) => {
        state.status = 'idle';
        state.onboardingUserData = action.payload;
      })
      .addCase(updateOnboardingUser.fulfilled, (state, action) => {
        state.status = 'idle';
        state.onboardingUserData = action.payload;

        if (action.payload._id) {
          setOnboardingUserIdToLocalStorage(action.payload._id);
        }
      });

    // Matchers
    builder
      .addMatcher(
        isPending(getOnboardingUser, updateOnboardingUser),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isRejected(getOnboardingUser, updateOnboardingUser),
        (state) => {
          state.status = 'rejected';
        }
      );
  },
});

export const { resetOnboardingUserData } = OnboardingSlice.actions;

export const getOnboardingState = (state: RootState): RootState['onboarding'] =>
  state.onboarding;

export * from './api';
export default OnboardingSlice.reducer;
